import * as bootstrap from "bootstrap";
import axios from 'axios';
import SimpleBar from "simplebar";
import { Notyf } from 'notyf';
import tippy from 'tippy.js';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/animations/shift-away.css';
import '@wooorm/starry-night/style/both';

window.bootstrap = bootstrap;
window.SimpleBar = SimpleBar;
window.tippy = tippy;

window.axios = axios;
window.axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

function escapeTooltipHtml(value) {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function formatTooltipContent(value) {
  const escapedValue = escapeTooltipHtml(value);

  return escapedValue
    .replace(/(https?:\/\/[^\s<]+)/g, match => {
      const trimmedMatch = match.replace(/[),.;:!?]+$/g, '');
      const trailingPunctuation = match.slice(trimmedMatch.length);

      if (!/[A-Za-z0-9]/.test(trimmedMatch.slice(trimmedMatch.indexOf('://') + 3))) {
        return match;
      }

      return `<a href="${trimmedMatch}" target="_blank" rel="noopener noreferrer">${trimmedMatch}</a>${trailingPunctuation}`;
    })
    .replace(/\n/g, '<br>');
}

export default class App {
  constructor(pageModuleLoaders = []) {
    this.pageModuleLoaders = pageModuleLoaders;
    this._notyfInitialized = false;
    this._flashListenerAttached = false;
    this._escapeKeyHandler = null;

    this._registerLivewireHooks();

    if (window.Livewire) {
      document.addEventListener('livewire:navigated', () => {
        this._uiInit();
      });
    } else {
      this.onLoad(() => this._uiInit());
    }
  }

  async _uiInit() {
    this._lHtml = document.documentElement;
    this._lPage = document.getElementById("page-container");

    if (!this._lPage) {
      console.warn('Page container not found');
      return;
    }

    this._lSidebar = document.getElementById("sidebar");
    this._lSidebarScrollCon = this._lSidebar?.querySelector(".js-sidebar-scroll");
    this._lSideOverlay = document.getElementById("side-overlay");
    this._lResize = null;
    this._lHeader = document.getElementById("page-header");
    this._lMain = document.getElementById("main-container");
    this._lFooter = document.getElementById("page-footer");

    this._initNotyf();
    this._initTippy();
    this._initToggleClass();
    this._initNavHandler();
    this._uiHandleSidebars();
    this._uiApiLayout();
    this._uiApiBlocks();
    await this._initPageModules();
  }

  async _initPageModules() {
    const tasks = this.pageModuleLoaders
      .filter(({ selector }) => document.querySelector(selector))
      .map(async ({ load }) => {
        const module = await load();

        if (typeof module === 'function') {
          module();
        }
      });

    await Promise.all(tasks);
  }

  _initNotyf() {
    const showValidationErrors = () => {
      const errors = Array.isArray(window.__validationErrors)
        ? window.__validationErrors
        : [];

      if (errors.length === 0 || !this.notyf) {
        return;
      }

      errors.forEach((error) => {
        const message = `${error ?? ''}`.trim();

        if (message !== '') {
          this.notyf.error(message);
        }
      });

      delete window.__validationErrors;
    };

    if (this._notyfInitialized) {
      const success = document.body.dataset.flashSuccess;
      const error = document.body.dataset.flashError;

      if (success) {
        this.notyf.success(success);
      }

      if (error) {
        this.notyf.error(error);
      }

      showValidationErrors();

      return;
    }

    this.notyf = new Notyf({
      duration: 3000,
      ripple: true,
      dismissible: true,
      position: { x: 'right', y: 'top' },
      types: [
        {
          type: 'error',
        },
        {
          type: 'success',
          background: '#4CAF50',
        },
      ]
    });

    this._notyfInitialized = true;

    const success = document.body.dataset.flashSuccess;
    const error = document.body.dataset.flashError;

    if (success) {
      this.notyf.success(success);
    }

    if (error) {
      this.notyf.error(error);
    }

    showValidationErrors();

    if (!this._flashListenerAttached) {
      window.addEventListener('flash', event => {
        const { type, message } = event.detail;

        if (type === 'success') {
          this.notyf.success(message);
        } else if (type === 'error') {
          this.notyf.error(message);
        } else {
          this.notyf.open({ type, message });
        }
      });

      this._flashListenerAttached = true;
    }
  }

  _initTippy() {
    document.querySelectorAll('[utp]').forEach(el => {
      if (el._tippy) {
        el._tippy.destroy();
      }

      const rawContent = el.getAttribute('utp') ?? '';
      const hasLink = /(https?:\/\/[^\s<]*[A-Za-z0-9][^\s<]*)/.test(rawContent);

      tippy(el, {
        content: formatTooltipContent(rawContent),
        arrow: true,
        allowHTML: true,
        animation: 'shift-away',
        interactive: el.dataset.utpInteractive === 'true' || hasLink,
        theme: 'nox',
        trigger: 'mouseenter focus click',
      });
    });
  }

  _registerLivewireHooks() {
    const attachHooks = () => {
      if (!window.Livewire || document.body.dataset.appLivewireTooltipHooked === '1') {
        return;
      }

      document.body.dataset.appLivewireTooltipHooked = '1';

      window.Livewire.hook('morph.updated', () => {
        this._initTippy();
      });
    };

    if (window.Livewire) {
      attachHooks();
    } else {
      document.addEventListener('livewire:initialized', attachHooks, { once: true });
    }
  }

  _initToggleClass() {
    const selector = '[data-toggle="class-toggle"]:not(.js-class-toggle-enabled), .js-class-toggle:not(.js-class-toggle-enabled)';

    document.querySelectorAll(selector).forEach(el => {
      el.classList.add("js-class-toggle-enabled");

      el.addEventListener("click", () => {
        const cssClasses = el.dataset.class?.split(" ");
        const targets = document.querySelectorAll(el.dataset.target);

        if (cssClasses && targets.length > 0) {
          targets.forEach(targetEl => {
            cssClasses.forEach(cls => targetEl.classList.toggle(cls));
          });
        }
      });
    });
  }

  _initNavHandler() {
    document.querySelectorAll('[data-toggle="submenu"]').forEach(link => {
      if (link.dataset.bound) {
        return;
      }

      link.dataset.bound = "1";

      link.addEventListener("click", (e) => {
        e.preventDefault();

        const mainNav = link.closest(".nav-main");
        const isDesktopHorizontal = window.innerWidth > 991 &&
          mainNav?.classList.contains("nav-main-horizontal") &&
          mainNav?.classList.contains("nav-main-hover");

        if (!isDesktopHorizontal) {
          const parentLi = link.closest("li");
          const isOpen = parentLi.classList.contains("open");

          if (isOpen) {
            parentLi.classList.remove("open");
            link.setAttribute("aria-expanded", "false");
          } else {
            Array.from(link.closest("ul").children).forEach(el => el.classList.remove("open"));
            parentLi.classList.add("open");
            link.setAttribute("aria-expanded", "true");
          }
        }
      });
    });
  }

  _uiApiLayout(mode = "init") {
    const layoutAPI = {
      init: () => {
        const buttons = document.querySelectorAll('[data-toggle="layout"]');

        buttons.forEach((btn) => {
          btn.addEventListener("click", () => {
            this._uiApiLayout(btn.dataset.action);
          });
        });

        if (this._lPage?.classList.contains("enable-page-overlay")) {
          const pageOverlay = document.createElement("div");
          pageOverlay.id = "page-overlay";

          this._lPage.insertBefore(pageOverlay, this._lPage.firstChild);

          pageOverlay.addEventListener("click", () => {
            this._uiApiLayout("side_overlay_close");
          });
        }
      },

      sidebar_toggle: () => {
        const className = window.innerWidth > 991 ? "sidebar-o" : "sidebar-o-xs";
        this._lPage.classList.toggle(className);
      },

      sidebar_open: () => {
        const className = window.innerWidth > 991 ? "sidebar-o" : "sidebar-o-xs";
        this._lPage.classList.add(className);
      },

      sidebar_close: () => {
        const className = window.innerWidth > 991 ? "sidebar-o" : "sidebar-o-xs";
        this._lPage.classList.remove(className);
      },

      side_overlay_toggle: () => {
        if (this._lPage.classList.contains("side-overlay-o")) {
          this._uiApiLayout("side_overlay_close");
        } else {
          this._uiApiLayout("side_overlay_open");
        }
      },

      side_overlay_open: () => {
        if (this._escapeKeyHandler) {
          document.removeEventListener("keydown", this._escapeKeyHandler);
        }

        this._escapeKeyHandler = (e) => {
          if (e.key === "Escape") {
            this._uiApiLayout("side_overlay_close");
          }
        };

        document.addEventListener("keydown", this._escapeKeyHandler);
        this._lPage.classList.add("side-overlay-o");
      },

      side_overlay_close: () => {
        if (this._escapeKeyHandler) {
          document.removeEventListener("keydown", this._escapeKeyHandler);
          this._escapeKeyHandler = null;
        }

        this._lPage.classList.remove("side-overlay-o");
      },
    };

    layoutAPI[mode]?.();
  }

  _uiApiBlocks(mode = "init", block = false) {
    const iconBase = "si";
    const iconFullscreen = "si-size-fullscreen";
    const iconFullscreenActive = "si-size-actual";
    const iconContent = "si-arrow-up";
    const iconContentActive = "si-arrow-down";

    let elBlock, btnFullscreen, btnContentToggle;

    const blockAPI = {
      init: () => {
        document.querySelectorAll('[data-toggle="block-option"][data-action="fullscreen_toggle"]').forEach((btn) => {
          const parentBlock = btn.closest(".block");
          const isFullscreen = parentBlock?.classList.contains("block-mode-fullscreen");
          btn.innerHTML = `<i class="${iconBase} ${isFullscreen ? iconFullscreenActive : iconFullscreen}"></i>`;
        });

        document.querySelectorAll('[data-toggle="block-option"][data-action="content_toggle"]').forEach((btn) => {
          const parentBlock = btn.closest(".block");
          const isHidden = parentBlock?.classList.contains("block-mode-hidden");
          btn.innerHTML = `<i class="${iconBase} ${isHidden ? iconContentActive : iconContent}"></i>`;
        });

        document.querySelectorAll('[data-toggle="block-option"]').forEach((btn) => {
          btn.addEventListener("click", () => {
            this._uiApiBlocks(btn.dataset.action, btn.closest(".block"));
          });
        });
      },

      fullscreen_toggle: () => {
        elBlock.classList.toggle("block-mode-fullscreen");

        if (btnFullscreen) {
          const icon = btnFullscreen.querySelector("i");

          if (elBlock.classList.contains("block-mode-fullscreen")) {
            icon?.classList.replace(iconFullscreen, iconFullscreenActive);
          } else {
            icon?.classList.replace(iconFullscreenActive, iconFullscreen);
          }
        }
      },

      fullscreen_on: () => {
        elBlock.classList.add("block-mode-fullscreen");
        btnFullscreen?.querySelector("i")?.classList.replace(iconFullscreen, iconFullscreenActive);
      },

      fullscreen_off: () => {
        elBlock.classList.remove("block-mode-fullscreen");
        btnFullscreen?.querySelector("i")?.classList.replace(iconFullscreenActive, iconFullscreen);
      },

      content_toggle: () => {
        elBlock.classList.toggle("block-mode-hidden");

        if (btnContentToggle) {
          const icon = btnContentToggle.querySelector("i");

          if (elBlock.classList.contains("block-mode-hidden")) {
            icon?.classList.replace(iconContent, iconContentActive);
          } else {
            icon?.classList.replace(iconContentActive, iconContent);
          }
        }
      },

      content_hide: () => {
        elBlock.classList.add("block-mode-hidden");
        btnContentToggle?.querySelector("i")?.classList.replace(iconContent, iconContentActive);
      },

      content_show: () => {
        elBlock.classList.remove("block-mode-hidden");
        btnContentToggle?.querySelector("i")?.classList.replace(iconContentActive, iconContent);
      },

      state_toggle: () => {
        elBlock.classList.toggle("block-mode-loading");

        if (elBlock.querySelector('[data-toggle="block-option"][data-action="state_toggle"][data-action-mode="demo"]')) {
          setTimeout(() => {
            elBlock.classList.remove("block-mode-loading");
          }, 2000);
        }
      },

      close: () => {
        elBlock.classList.add("d-none");
      },

      open: () => {
        elBlock.classList.remove("d-none");
      },
    };

    if (mode === "init") {
      blockAPI.init();
    } else {
      elBlock = block instanceof Element ? block : document.querySelector(block);

      if (elBlock) {
        btnFullscreen = elBlock.querySelector('[data-toggle="block-option"][data-action="fullscreen_toggle"]');
        btnContentToggle = elBlock.querySelector('[data-toggle="block-option"][data-action="content_toggle"]');

        blockAPI[mode]?.();
      }
    }
  }

  _uiHandleSidebars(mode = "init") {
    if (!this._lSidebar && !this._lSideOverlay) {
      return;
    }

    if (mode === "init") {
      this._lPage.classList.add("side-trans-enabled");

      window.addEventListener("resize", () => {
        clearTimeout(this._lResize);
        this._lPage.classList.remove("side-trans-enabled");

        this._lResize = setTimeout(() => {
          this._lPage.classList.add("side-trans-enabled");
        }, 500);
      });

      this._uiHandleSidebars("custom-scroll");
    } else if (mode === "custom-scroll") {
      if (this._lPage.classList.contains("side-scroll")) {
        if (this._lSidebar && !this._lSidebarScroll && this._lSidebarScrollCon) {
          this._lSidebarScroll = new SimpleBar(this._lSidebarScrollCon);
        }

        if (this._lSideOverlay && !this._lSideOverlayScroll) {
          this._lSideOverlayScroll = new SimpleBar(this._lSideOverlay);
        }
      }
    }
  }

  onLoad(fn) {
    if (document.readyState !== "loading") {
      fn();
    } else {
      document.addEventListener("DOMContentLoaded", fn);
    }
  }

  init() {
    this._uiInit();
  }

  block(mode, block) {
    this._uiApiBlocks(mode, block);
  }
}
